/**
 * `ScriptTagInstallPanel` — body of the `script_tag` install-method
 * tab inside `InstallMethodTabs` (ADR-0042 §2). Extracted from the
 * prior `InstallStep` snippet+detector block (the universally-
 * applicable default tab — paste a script before `</body>` and the
 * embed identifies agent traffic).
 *
 * ADR-0050 §1.2 — this panel owns the SINGLE rendered install snippet
 * in Step 3. The snippet carries `data-site-key` AND (when known)
 * `data-proof-token`; the Verify control is rendered inline next to
 * Copy via the shared `useProofCheck` state passed in by the composing
 * step (`InstallVerifyStep`). `ProofStatusPanel` no longer renders a
 * second copy of the snippet.
 *
 * Reuses `buildScriptSnippet(site_key, proof_token?)` + `InstallDetector`
 * verbatim; wire contracts unchanged. The "site key unavailable /
 * rotate" copy from the prior `InstallStep` is preserved — the merchant
 * who lands here without a key in session (refresh, bfcache) sees the
 * explicit rotate affordance instead of an empty snippet.
 *
 * The ADR-0050 §1.1 `custom_embed` fallback disclosure ("Can't add a
 * script tag? Use server-side ingest") is HIDDEN as of 2026-07-07
 * (founder decision after live-testing): the backend adapter has not
 * shipped, so the disclosure read as a working option and dead-ended
 * merchants who tried it. `CustomEmbedInstallPanel` stays in the tree;
 * reinstate the disclosure when the adapter ships (ADR-0041 sibling
 * slice).
 */

import {
  ArrowRight,
  Check,
  ChevronDown,
  Copy,
  Download,
  RefreshCw,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useApi } from '../../api/api-context.js';
import { useSites, useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { buildAgentCardSnippet, buildAgentCardUrl, buildScriptSnippet, downloadDiscoveryJson, isValidAgentCardKey } from '../../lib/install-snippet.js';
import { REDIRECT_HOSTS, redirectInstructions, type RedirectHost } from '../../lib/agent-card-redirect.js';
import { Card, Collapsible, StepHeading } from '../../components/atoms/index.js';
import { SCRIPT_SENTENCE, SCRIPT_WHAT, SCRIPT_WHAT_BODY, STATIC_CARD_OTHER, STATIC_CARD_SENTENCE, VERIFY_HINT } from './install-copy.js';
import type { UseProofCheckState } from '../../hooks/useProofCheck.js';
import { InstallDetector } from './InstallDetector.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface ScriptTagInstallPanelProps {
  /**
   * ADR-0211 §6.3 — the host whose redirect snippet Step 2 preselects,
   * from the detector's `hosting_hint` / `vendor_hint`. The merchant can
   * pick any other host; omitted → 'generic'.
   */
  redirectHost?: RedirectHost | undefined;
  /** Forwarded to `InstallDetector` — fires once when the first agent
   *  ping arrives. The composing step uses this to surface the
   *  Continue affordance. */
  onInstalled?: () => void;
  /** Shared proof-check state from `useProofCheck`. When present, the
   *  panel:
   *    - threads `data.proof_token` into the rendered snippet via
   *      `buildScriptSnippet(site_key, proof_token)` so the merchant
   *      pastes ONE artifact carrying both attributes (ADR-0050 §1.2);
   *    - renders the Verify control inline as the primary affordance
   *      next to Copy.
   *  Optional only to preserve compile-time compatibility with callers
   *  that pre-date ADR-0050. When omitted, the panel renders the
   *  snippet without the proof-token attribute and without an inline
   *  Verify button — the legacy two-component composition. */
  proofCheck?: UseProofCheckState;
}

export function ScriptTagInstallPanel({
  onInstalled,
  proofCheck,
  redirectHost,
}: ScriptTagInstallPanelProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  const { activeSite, setActiveSite, updateSite } = useSites();
  const [copied, setCopied] = useState(false);
  // Separate copied state for the static `<head>` link snippet (Option A)
  // so its Copy button toasts independently of the main snippet's.
  const [copiedCard, setCopiedCard] = useState(false);
  const [rotating, setRotating] = useState(false);
  // Step 3 (the <head> rel-tag) is an optional fallback for merchants who
  // can't upload a file — collapsed by default so Step 2 (the card upload)
  // stays the primary action (founder 2026-07-29).
  const [showHeadTag, setShowHeadTag] = useState(false);
  // ADR-0211 §6.3 — Step 2 option A: a redirect rule (never goes stale)
  // before option B (upload the file). Host chips preselect from the
  // detector's hint; a merchant click wins.
  const [redirect_host, setRedirectHost] = useState<RedirectHost>(redirectHost ?? 'generic');
  const [copiedRedirect, setCopiedRedirect] = useState(false);
  // Detection resolves asynchronously after mount; without this sync
  // the initial `useState(redirectHost ?? 'generic')` fires once and a
  // later detector result (e.g. Netlify) never pre-selects the chip.
  // A merchant click wins — once `userPickedHostRef` is true the
  // detector no longer overrides their choice.
  const userPickedHostRef = useRef(false);
  useEffect(() => {
    if (userPickedHostRef.current) return;
    if (redirectHost !== undefined && redirectHost !== redirect_host) {
      setRedirectHost(redirectHost);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectHost]);
  // ADR-0197 / founder 2026-07-28 — the static-card step (Step 2) is
  // ALWAYS visible, never behind a disclosure: a merchant who pastes only
  // the script and stops is invisible to static crawlers / readiness
  // checkers. (Was a `showStaticDiscovery` toggle defaulting closed, which
  // hid the agent-card download so the panel read as a single step.)
  // Founder 2026-10-28: the raw key is shown once, but the snippet is
  // public code. Fall back to the site_key_hash, which the edge accepts
  // as the same site (ADR-0055), so the snippet never disappears.
  const site_key = isValidAgentCardKey(activeSite?.site_key)
    ? (activeSite?.site_key ?? '')
    : (activeSite?.site_key_hash ?? '');
  const has_key = isValidAgentCardKey(site_key);
  const proof_token = proofCheck?.data?.proof_token;
  const snippet = buildScriptSnippet(site_key, proof_token);

  const verifyDisabled =
    proofCheck === undefined
    || activeSite === null
    || proofCheck.checking
    || proofCheck.verified
    || proofCheck.loading
    || proofCheck.data === null;

  const rotate = async (): Promise<void> => {
    if (activeSite === null || rotating) return;
    setRotating(true);
    try {
      const rotated = await api.sites.rotateKey(activeSite.site_id);
      updateSite(rotated.site_id, rotated);
      setActiveSite(rotated);
      // The proof-token is per-site and stable across key rotation —
      // no reload needed unless the merchant explicitly rotates the
      // token surface (a separate, currently-unimplemented operation).
      toast({
        title: 'New install key generated',
        body: 'Paste the updated snippet on your site. If you deployed the previous key anywhere, replace it now — the old one no longer works.',
        tone: 'lime',
      });
    } catch (err) {
      toast({
        title: 'Could not generate install key',
        body: err instanceof Error ? err.message : 'Unknown error',
        tone: 'coral',
      });
    } finally {
      setRotating(false);
    }
  };

  const copy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(snippet);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const copyRedirect = (text: string): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(text);
    }
    setCopiedRedirect(true);
    setTimeout(() => setCopiedRedirect(false), 1800);
  };

  const copyCard = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(buildAgentCardSnippet(site_key));
    }
    setCopiedCard(true);
    setTimeout(() => setCopiedCard(false), 1800);
  };

  return (
    <div>
      {!has_key ? (
        <Card className="p-5 mb-4" style={{ borderColor: C.amberFg }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.amberFg,
              letterSpacing: 2,
              marginBottom: 8,
            }}
          >
            INSTALL KEY UNAVAILABLE
          </div>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              lineHeight: 1.55,
              marginBottom: 14,
            }}
          >
            Your site key is shown only once at creation. We can't see it anymore. Generate a new one to display the install snippet. <strong>If you already pasted the previous key on a live site, you'll need to replace it with the new value — the old key stops working immediately.</strong>
          </p>
          <button
            type="button"
            onClick={() => void rotate()}
            disabled={rotating || activeSite === null}
            className="flex items-center gap-2 px-4 py-2.5"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              opacity: rotating || activeSite === null ? 0.6 : 1,
            }}
          >
            {rotating ? (
              <>
                <RefreshCw
                  size={14}
                  style={{ animation: 'spin 1.4s linear infinite' }}
                />
                Generating…
              </>
            ) : (
              'Generate install key'
            )}
          </button>
        </Card>
      ) : null}

      {/* Step 1 — add the script (browser agents + analytics). */}
      <StepHeading n={1}>Add the script</StepHeading>
      {/* ADR-0212 — one sentence above the fold; the how/why behind a
          disclosure. */}
      <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.55, margin: '0 0 12px', maxWidth: '60ch' }}>
        {SCRIPT_SENTENCE}
      </p>
      <div style={{ marginBottom: 12 }}>
        <Collapsible variant="inline" label={SCRIPT_WHAT}>
          <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: 0, maxWidth: 560 }}>
            {SCRIPT_WHAT_BODY}
          </p>
        </Collapsible>
      </div>
      <div
        className="relative p-4 mb-4"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          opacity: has_key ? 1 : 0.45,
          pointerEvents: has_key ? undefined : 'none',
        }}
        data-testid="install-snippet-block"
      >
        <button
          type="button"
          onClick={copy}
          aria-label="Copy snippet"
          className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1"
          style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            fontFamily: monoFont,
            fontSize: 10,
            color: copied ? C.limeFg : C.muted,
            borderRadius: 2,
          }}
        >
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'COPIED' : 'COPY'}
        </button>
        <pre
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.text,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            paddingRight: 60,
            margin: 0,
          }}
          data-proof-token={
            typeof proof_token === 'string' && proof_token.length > 0
              ? proof_token
              : undefined
          }
        >
          {snippet}
        </pre>
      </div>

      {/* Static-discovery completeness state (2026-07-22 declutter +
          honesty pass, ADR-0192). The two prior gray boxes (AGENT
          DISCOVERY static `<head>` tag + WELL-KNOWN DISCOVERY download)
          collapsed into ONE disclosure. The HONEST headline is always
          visible: the script tag injects the agent-card `<link>` via
          JavaScript at runtime, so agents that render the page find it —
          but static crawlers and agent-readiness checkers read raw HTML
          and see nothing at `/.well-known/agent-card.json`. Serving the
          static card is what closes that gap. Copy names the CLASS of
          tool (static crawlers / readiness checkers), never a specific
          third-party site (durability + product voice). */}
      {has_key ? (
        <div
          className="mb-4 pt-5"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
          data-testid="static-discovery-section"
        >
          <StepHeading n={2}>Serve your agent card</StepHeading>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              lineHeight: 1.55,
              margin: '0 0 12px',
              maxWidth: '60ch',
            }}
          >
            {STATIC_CARD_SENTENCE}
          </p>

          <div data-testid="static-discovery-body">
            {/* ADR-0211 §6.3 — Option A (recommended): a redirect rule. */}
            {(() => {
              const card_url = buildAgentCardUrl(site_key);
              const ins = redirectInstructions(redirect_host, card_url);
              const text = ins.lines.join('\n');
              return (
                <div className="mt-3" data-testid="static-card-redirect">
                  <div
                    style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.limeFg, marginBottom: 6 }}
                  >
                    REDIRECT · RECOMMENDED
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2" role="tablist" aria-label="Your host">
                    {REDIRECT_HOSTS.map((h) => (
                      <button
                        key={h.id}
                        type="button"
                        role="tab"
                        aria-selected={h.id === redirect_host}
                        onClick={() => {
                          userPickedHostRef.current = true;
                          setRedirectHost(h.id);
                        }}
                        style={{
                          background: h.id === redirect_host ? C.surface2 : 'transparent',
                          border: `1px solid ${h.id === redirect_host ? C.text : C.border}`,
                          color: h.id === redirect_host ? C.text : C.muted,
                          fontFamily: monoFont,
                          fontSize: 9,
                          letterSpacing: 0.8,
                          borderRadius: 2,
                          padding: '3px 8px',
                          cursor: 'pointer',
                        }}
                        data-testid={`redirect-host-${h.id}`}
                      >
                        {h.label.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginBottom: 6 }}>{ins.where}</div>
                  {ins.kind === 'code' ? (
                    <div className="relative" style={{ minWidth: 0 }}>
                      {/* The Netlify / Apache / nginx one-liners are often
                       *  wider than the container at mobile widths. Wrap
                       *  on whitespace with a mono-friendly `pre-wrap`,
                       *  break long tokens (the signed hash URL) and keep
                       *  the horizontal-scroll fallback for anything that
                       *  still cannot wrap. */}
                      <pre
                        style={{
                          fontFamily: monoFont,
                          fontSize: 11,
                          color: C.text,
                          background: C.surface2,
                          border: `1px solid ${C.border}`,
                          borderRadius: 2,
                          padding: '8px 10px',
                          margin: 0,
                          overflowX: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                        data-testid="redirect-snippet"
                      >
                        {text}
                      </pre>
                      <button
                        type="button"
                        onClick={() => copyRedirect(text)}
                        className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5"
                        style={{
                          background: 'transparent',
                          border: `1px solid ${C.border}`,
                          color: C.text,
                          fontFamily: sansFont,
                          fontSize: 12,
                          borderRadius: 2,
                          cursor: 'pointer',
                        }}
                        data-testid="redirect-copy"
                      >
                        {copiedRedirect ? <Check size={12} /> : <Copy size={12} />}
                        {copiedRedirect ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  ) : (
                    <ol
                      style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.6, margin: 0, paddingLeft: 18 }}
                      data-testid="redirect-steps"
                    >
                      {ins.lines.map((l) => (
                        <li key={l} style={{ wordBreak: 'break-all' }}>{l}</li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })()}

            {/* ADR-0212 — the redirect above is the one recommended
                path; the file upload and the <head> tag are alternatives
                behind a single disclosure. */}
            <div style={{ marginTop: 14 }}>
              <Collapsible variant="inline" label={STATIC_CARD_OTHER}>
                <div data-testid="static-card-other-ways">
            <div
              style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, marginTop: 4 }}
            >
              UPLOAD THE FILE INSTEAD
            </div>
            <button
              type="button"
              onClick={() =>
                downloadDiscoveryJson({
                  name: activeSite?.name ?? activeSite?.domain ?? '',
                  url: `https://${activeSite?.domain ?? ''}`,
                  site_key,
                })
              }
              disabled={!has_key}
              className="flex items-center gap-2 px-3 py-1.5 mt-2"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 12,
                borderRadius: 2,
                cursor: has_key ? 'pointer' : 'default',
                opacity: has_key ? 1 : 0.5,
              }}
              data-testid="manual-step-card-download"
            >
              <Download size={12} />
              Download agent-card.json
            </button>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 11,
                color: C.dim,
                lineHeight: 1.5,
                marginTop: 8,
              }}
            >
              Your signed card:{' '}
              <a
                href={buildAgentCardUrl(site_key)}
                target="_blank"
                rel="noreferrer"
                style={{ color: C.muted, wordBreak: 'break-all' }}
              >
                {buildAgentCardUrl(site_key)}
              </a>
            </div>

            {/* Step 3 (optional, collapsed) — the <head> rel-tag fallback
                for merchants who can't upload a file. */}
            <button
              type="button"
              onClick={() => setShowHeadTag((v) => !v)}
              className="inline-flex items-center gap-1 mt-4"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
              }}
              aria-expanded={showHeadTag}
              data-testid="head-tag-toggle"
            >
              Can&apos;t upload files? Add a{' '}
              <span style={{ fontFamily: monoFont }}>&lt;head&gt;</span> tag instead
              <ChevronDown
                size={13}
                style={{
                  transform: showHeadTag ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.15s',
                }}
              />
            </button>
            {showHeadTag ? (
              <div className="mt-2">
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.muted,
                    lineHeight: 1.6,
                    marginBottom: 6,
                  }}
                >
                  For agents that read HTML but don&apos;t run JavaScript. Paste
                  in your theme&apos;s{' '}
                  <span style={{ fontFamily: monoFont }}>&lt;head&gt;</span>.
                </div>
                <div
                  className="relative p-3"
                  style={{
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                  }}
                >
                  <button
                    type="button"
                    onClick={copyCard}
                    aria-label="Copy static head tag"
                    className="absolute top-2.5 right-2.5 flex items-center gap-1.5 px-2 py-1"
                    style={{
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: copiedCard ? C.limeFg : C.muted,
                      borderRadius: 2,
                    }}
                  >
                    {copiedCard ? <Check size={10} /> : <Copy size={10} />}
                    {copiedCard ? 'COPIED' : 'COPY'}
                  </button>
                  <pre
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      color: C.text,
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0,
                      paddingRight: 60,
                    }}
                  >
                    {buildAgentCardSnippet(site_key)}
                  </pre>
                </div>
              </div>
            ) : null}
                </div>
              </Collapsible>
            </div>
          </div>
        </div>
      ) : null}

      {/* ADR-0050 §1.2 — inline Verify control. Only rendered when the
          composing step passed in a `useProofCheck` state (the
          production case). When the proof state is absent (legacy
          callers) we fall through to the prior layout. */}
      {proofCheck !== undefined ? (
        <div
          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4"
        >
          <button
            type="button"
            onClick={() => void proofCheck.verify()}
            disabled={verifyDisabled}
            className="flex items-center justify-center gap-2 px-5 py-2.5"
            style={{
              background: proofCheck.verified ? C.limeDim : C.lime,
              color: proofCheck.verified ? C.limeFg : C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 2,
              opacity: verifyDisabled && !proofCheck.verified ? 0.6 : 1,
              border: proofCheck.verified ? `1px solid ${C.limeFg}` : 'none',
            }}
            aria-label={proofCheck.verified ? 'Domain verified' : 'Verify domain'}
          >
            {proofCheck.checking ? (
              <>
                <RefreshCw
                  size={15}
                  style={{ animation: 'spin 1.4s linear infinite' }}
                />
                Checking…
              </>
            ) : proofCheck.verified ? (
              <>
                <Check size={15} /> Domain verified
              </>
            ) : (
              <>
                Verify <ArrowRight size={15} />
              </>
            )}
          </button>
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
              maxWidth: 420,
            }}
          >
            {VERIFY_HINT}
          </span>
        </div>
      ) : null}

      {onInstalled !== undefined ? (
        <InstallDetector onConnected={onInstalled} />
      ) : (
        <InstallDetector />
      )}

      {/* Server-side ingest disclosure REMOVED (founder decision,
          2026-07-07 live-testing): the backend adapter for the
          `custom_embed` path has not shipped (ADR-0041 sibling slice),
          so offering "use server-side ingest" here read as a working
          option and dead-ended a merchant who tried it. Reinstate the
          ADR-0050 §1.1 disclosure (`CustomEmbedInstallPanel`, kept in
          the tree) when the adapter ships. */}
    </div>
  );
}
